import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import type { Role } from '@grc/shared';
import { ErrorLine } from './components/Lines';
import { errorText } from './errors';
import { t, type Code } from './i18n';
import { useMember, useSession } from './session';

// The frame around every screen behind the login (ARCHITECTURE → The web app's structure; D22):
// the top bar with the club's name and Log out, and the main menu, which is tabs at the bottom on
// a phone and a side nav on a desktop (styles.css switches at 48rem). The screen goes in <Outlet/>.

type NavItem = {
  to: string;
  label: Code;
  /** Only these roles see the item: the admin's screens. */
  roles?: readonly Role[];
};

/**
 * Release 1's tabs, in order (the owner's choice, 2026-10-16: all of them from the start, the
 * screens not built yet saying "Coming soon"). Each step that builds a screen replaces its
 * placeholder in App.tsx.
 */
export const NAV: readonly NavItem[] = [
  { to: '/', label: 'nav.home' },
  { to: '/events', label: 'nav.events' },
  { to: '/boards', label: 'nav.boards' },
  { to: '/profile', label: 'nav.profile' },
  { to: '/admin/members', label: 'nav.members', roles: ['admin'] },
];

export function Shell() {
  const member = useMember();
  const { logout } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function onLogout() {
    setBusy(true);
    setError(undefined);
    try {
      // The guard sends the member to the login once the session has ended.
      await logout();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const items = NAV.filter((item) => item.roles === undefined || item.roles.includes(member.role));

  return (
    <div className="shell">
      <header className="topbar">
        <span className="topbar-name">{t('app.name')}</span>
        <button type="button" className="topbar-button" onClick={onLogout} disabled={busy}>
          {t('shell.logout')}
        </button>
      </header>
      <nav className="nav" aria-label={t('nav.label')}>
        <ul>
          {items.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.to === '/'}>
                {t(item.label)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <main className="content">
        <ErrorLine text={error} />
        <Outlet />
      </main>
    </div>
  );
}
